import { keysOf } from './keys.of';
import { isNullOrWhitespace } from './strings';

/**
 * Accent folding, kept apart from the general string helpers on purpose: `strings.ts` sits on the
 * main app's eager boot path (`format` and `isNullOrWhitespace` run during startup), whereas the
 * accent table below is only needed by the search and filter surfaces that fold user input. In its
 * own module the table ships only with the bundles that call `foldAccents`, not with every bundle
 * that formats a string.
 */

const reverseAccentsMap = {
    A: 'АÁÀĂẮẰẴẲÂẤẦẪẨǍÅǺÄǞÃȦǠĄĀẢȀȂẠẶẬḀȺǼǢ',
    a: 'аáàăắằẵẳâấầẫẩǎåǻäǟãȧǡąāảȁȃạặậḁⱥǽǣẚ',
    B: 'БḂḄḆɃƁƃ',
    b: 'бḃḅḇƀɓᵬ',
    C: 'ĆĈČĊÇḈȻƇ',
    c: 'ćĉčċçḉȼƈɕ',
    D: 'ĎḊḐḌḒḎĐƉƊƋƌ',
    d: 'ďḋḑḍḓḏđᵭɖɗȡð',
    E: 'ÈÉƏĔÊẾỀỄỂĚËẼĖȨḜĘĒḖḔẺȄȆẸỆḘḚɆƎ',
    e: 'éèǝĕêếềễểěëẽėȩḝęēḗḕẻȅȇẹệḙḛɇɚɝ',
    F: 'ФḞƑ',
    f: 'фḟᵮƒ',
    G: 'ГǴĞĜǦĠĢḠǤƓ',
    g: 'гǵğĝǧġģḡǥɠ',
    H: 'ĤȞḦḢḨḤḪH̱ĦⱧ',
    h: 'ĥȟḧḣḩḥḫẖħⱨ',
    I: 'ÍÌĬÎǏÏḮĨİĮĪỈȈȊỊḬIƗ',
    i: 'íìĭîǐïḯĩiįīỉȉȋịḭıɨ',
    J: 'ĴJJ̌Ɉ',
    j: 'ĵȷǰɉʝɟʄ',
    K: 'ḰǨĶḲḴƘⱩ',
    k: 'ḱǩķḳḵƙⱪ',
    L: 'ĹĽĻḶḸḼḺĿȽⱠⱢꞭ',
    l: 'ĺľļḷḹḽḻŀƚⱡɫɬɭȴ',
    M: 'ḾṀṂⱮ',
    m: 'ḿṁṃɱ',
    N: 'ŃǸŇÑṄŅṆṊṈƝȠ',
    n: 'ńǹňñṅņṇṋṉɲƞɳȵ',
    O: 'ÓÒŎÔỐỒỖỔǑÖȪŐÕṌṎȬȮȰØǾǪǬŌṒṐỎȌȎƠỚỜỠỞỢỌỘƟ',
    o: 'óòŏôốồỗổǒöȫőõṍṏȭȯȱøǿǫǭōṓṑỏȍȏơớờỡởợọộɵ',
    P: 'ṔṖⱣƤ',
    p: 'ṕṗᵽƥ',
    Q: 'Ɋ',
    q: 'ɋʠ',
    R: 'ŔŘṘŖȐȒṚṜṞɌⱤ',
    r: 'ŕřṙŗȑȓṛṝṟɍɼɽɾᵳᵲ',
    S: 'ŚṤŜŠṦṠŞṢṨȘ',
    s: 'śṥŝšṧṡşṣṩșʂẛß',
    T: 'ÞŤT̈ṪŢṬȚṰṮŦȾƬƮ',
    t: 'þťẗṫţṭțṱṯŧⱦƭʈȶƫᵵ',
    U: 'ÚÙŬÛǓŮÜǗǛǙǕŰŨṸŲŪṺỦȔȖƯỨỪỮỬỰỤṲṶṴɄ',
    u: 'úùŭûǔůüǘǜǚǖűũṹųūṻủȕȗưứừữửựụṳṷṵʉ',
    V: 'ṼṾƲ',
    v: 'ṽṿʋ',
    W: 'ẂẀŴWẄẆẈ',
    w: 'ẃẁŵẘẅẇẉ',
    X: 'ẌẊ',
    x: 'ẍẋ',
    Y: 'ÝỲŶY̊ŸỸẎȲỶỴɎƳ',
    y: 'ýỳŷẙÿỹẏȳỷỵɏƴʏ',
    Z: 'ŹẐŽŻẒẔƵȤⱫǮ',
    z: 'źẑžżẓẕƶȥʐʑⱬƺ',
};

const accentMap: Record<string, keyof typeof reverseAccentsMap> = {};
keysOf(reverseAccentsMap).forEach(toChar => {
    for (const char of reverseAccentsMap[toChar]) {
        accentMap[char] = toChar;
    }
});

/**
 * Folds accents in string.
 * Replaces non-latin characters with their latin equivalents.
 *
 * @param input - input string
 * @returns new folded string
 */
function foldAccents(input: string) {
    if (isNullOrWhitespace(input)) {
        return input;
    }

    return Array.from(input)
        .map(char => accentMap[char] ?? char)
        .join('');
}

export { foldAccents };
